/**
 * Address heuristics for billing entities.
 *
 * The billing address on a claim is the X12 837P 2010AA loop, which payers
 * require to be a *physical street location*. Submitting a PO Box or mailbox
 * service there gets the claim rejected (Stedi submission error 33). The
 * pay-to address (2010AB) is where a mailbox is legitimate.
 *
 * This detector is advisory only — it drives an inline warning in the form and
 * never blocks a save. The backend does the authoritative rejection; keeping
 * this list deliberately close to the backend's patterns just means the user
 * usually finds out before they hit submit.
 */

/**
 * Word-bounded, case-insensitive patterns for the mailbox forms payers reject
 * in the 2010AA billing address.
 *
 * Deliberately narrow: a bare "Box" is a real street name ("Box Elder Rd"), and
 * "Boxwood Ln" / "Lombard St" must not trip the warning.
 */
export const MAILBOX_ADDRESS_PATTERNS: readonly RegExp[] = [
  // "PO Box", "P.O. Box", "P O Box", "P.O.Box", "POBox"
  /\bp\.?\s*o\.?\s*box\b/i,
  // "Post Office Box"
  /\bpost\s+office\s+box\b/i,
  // "Lock Box", "Lockbox"
  /\block\s*box\b/i,
  // "PMB 214" — the private-mailbox number used by mailbox services
  /\bpmb\b/i,
  // "Private Mail Box", "Private Mailbox"
  /\bprivate\s+mail\s*box\b/i,
];

/**
 * Whether either address line looks like a PO Box / mailbox service rather than
 * a physical street address.
 *
 * @param line1 - Address line 1 (street).
 * @param line2 - Address line 2 (suite/unit) — mailbox numbers often hide here.
 * @returns true when the address should be flagged as a probable mailbox.
 */
export function looksLikeMailboxAddress(
  line1?: string | null,
  line2?: string | null,
): boolean {
  const candidates = [line1, line2].filter(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  );

  return candidates.some(value => MAILBOX_ADDRESS_PATTERNS.some(pattern => pattern.test(value)));
}
